import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import jwtDecode from "jwt-decode";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import { MODULE_REGISTRY } from "../registry";
import "./AuthPages.css";

const TYPE_LABELS = {
  COMPANY_INVITE: "Welcome! Set your password to get started.",
  USER_INVITE: "Your account is ready. Set a password to log in.",
  FORGOT_PASSWORD: "Enter your new password below.",
  ADMIN_RESET: "Your password was reset. Choose a new one to continue.",
};

export default function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAccessToken, setTenantModule, updateUser } = useAuth();

  const token = searchParams.get("token") || "";

  const [status, setStatus] = useState("verifying"); // verifying | valid | invalid | success
  const [tokenInfo, setTokenInfo] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    api
      .get(`/auth/token/${token}`)
      .then((res) => {
        if (res.data.valid) {
          setTokenInfo(res.data);
          setStatus("valid");
        } else {
          setStatus("invalid");
        }
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post(`/auth/token/${token}/use`, { newPassword });
      const { accessToken, refreshToken, user } = res.data;
      const decoded = jwtDecode(accessToken);
      const module = decoded.tenantModule || "dms";
      setAccessToken(accessToken);
      localStorage.setItem("accessToken", accessToken);
      if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
      setTenantModule(module);
      updateUser(user);
      setStatus("success");
      const defaultRoute = (MODULE_REGISTRY[module] || MODULE_REGISTRY.dms).defaultRoute;
      setTimeout(() => navigate(defaultRoute), 1500);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const subtitle = tokenInfo
    ? TYPE_LABELS[tokenInfo.type] || "Set your new password."
    : "";

  return (
    <div className="auth-shell">
      <section className="auth-main">
        <div className="auth-container">
          {status === "verifying" && (
            <div className="auth-header">
              <p>Verifying your link...</p>
            </div>
          )}

          {status === "invalid" && (
            <div className="auth-header">
              <h1>Link Invalid or Expired</h1>
              <p>
                This password link is no longer valid. It may have already been
                used or expired.
              </p>
              <div className="auth-links" style={{ marginTop: 16 }}>
                <Link to="/forgot-password">Request a new link</Link>
                <Link to="/login">Back to login</Link>
              </div>
            </div>
          )}

          {status === "valid" && (
            <>
              <div className="auth-header">
                <h1>Set Your Password</h1>
                {tokenInfo?.name && (
                  <p>
                    Hi <strong>{tokenInfo.name}</strong>. {subtitle}
                  </p>
                )}
              </div>
              <form onSubmit={handleSubmit} className="auth-form">
                {error && <div className="auth-alert">{error}</div>}
                <div className="auth-field">
                  <label>New Password</label>
                  <input
                    className="auth-input"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    required
                    autoFocus
                  />
                </div>
                <div className="auth-field">
                  <label>Confirm Password</label>
                  <input
                    className="auth-input"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat your password"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="auth-btn auth-btn-primary"
                  disabled={loading}
                  style={{ marginTop: 8 }}
                >
                  {loading ? "Setting password..." : "Set Password & Log In"}
                </button>
              </form>
            </>
          )}

          {status === "success" && (
            <div className="auth-header">
              <h1>Password Set!</h1>
              <p>You're now logged in. Redirecting you to your workspace...</p>
            </div>
          )}
          <p className="auth-copyright">
            © 2026 DecisionFlow. All rights reserved.
          </p>
        </div>
      </section>
      <aside className="auth-side">
        <h2>Secure Access</h2>
        <p>
          Use a strong password with a mix of uppercase, lowercase, numbers, and
          symbols to keep your account safe.
        </p>
      </aside>
    </div>
  );
}
